import { normalizeLedgerFields } from "../lib/ledger-field-compat";
import type { DashboardData, Owner } from "../types";
import { canChangePending, readPendingEntries } from "./pending-entries";
import { readPendingReceipts } from "./pending-receipts";
import { readPendingRecords } from "./pending-records";

const DASHBOARD_KEY = "barnyard-offline-dashboard";
const PROFILE_KEY = "barnyard-offline-profile";

export type OfflineDashboard = {
  data: DashboardData;
  savedAt: string;
};

export type PendingSyncItem = {
  key: string;
  owner: Owner;
  type: string;
  amount: string;
  accountId: string;
  date: string;
  label: string;
  status: "waiting" | "partial";
  needsReview?: boolean;
};

/** Older offline caches and partial payloads omit list fields; keep every screen iterable. */
export function normalizeDashboardData(data: DashboardData): DashboardData {
  data = normalizeLedgerFields(data);
  const entry = data.entryOptions;
  return {
    ...data,
    accounts: data.accounts || [],
    transactions: data.transactions || [],
    payees: data.payees || [],
    ious: data.ious || [],
    recurring: data.recurring || [],
    rates: data.rates || {},
    budgets: data.budgets || [],
    reconciliations: data.reconciliations || [],
    reconciliationCompletions: data.reconciliationCompletions || [],
    entryOptions: {
      categories: entry?.categories || [],
      subcategories: entry?.subcategories || [],
      subcategoriesByCategory: entry?.subcategoriesByCategory || {},
      incomeCategories: entry?.incomeCategories || [],
      incomeSubcategories: entry?.incomeSubcategories || [],
      incomeSubcategoriesByCategory: entry?.incomeSubcategoriesByCategory || {},
    },
    warnings: data.warnings || [],
  };
}

export function saveOfflineProfile(profile: Owner) {
  try {
    localStorage.setItem(PROFILE_KEY, profile);
  } catch {
    /* Offline cache is best-effort. */
  }
}

export function readOfflineProfile(): Owner | null {
  try {
    const profile = localStorage.getItem(PROFILE_KEY);
    return profile && profile.length <= 40 ? profile : null;
  } catch {
    return null;
  }
}

export function clearOfflineProfile() {
  try {
    localStorage.removeItem(PROFILE_KEY);
  } catch {
    /* Offline cache is best-effort. */
  }
}

export function saveOfflineDashboard(data: DashboardData) {
  try {
    localStorage.setItem(
      DASHBOARD_KEY,
      JSON.stringify({ data, savedAt: new Date().toISOString() }),
    );
  } catch {
    /* Offline cache is best-effort. */
  }
}

export function readOfflineDashboard(): OfflineDashboard | null {
  try {
    const value = JSON.parse(
      localStorage.getItem(DASHBOARD_KEY) || "null",
    ) as OfflineDashboard | null;
    if (
      value &&
      typeof value.savedAt === "string" &&
      value.data &&
      Array.isArray(value.data.accounts) &&
      Array.isArray(value.data.transactions)
    ) {
      value.data = normalizeDashboardData(value.data);
      return value;
    }
  } catch {
    /* A broken cache should behave like no cache. */
  }
  return null;
}

export function clearOfflineDashboard() {
  try {
    localStorage.removeItem(DASHBOARD_KEY);
  } catch {
    /* Offline cache is best-effort. */
  }
}

export function readPendingSyncItems(): PendingSyncItem[] {
  try {
    const entries = readPendingEntries().map(({ key, entry }) => ({
      key, owner: entry.owner, type: entry.type || "Expense", amount: entry.amount,
      accountId: entry.accountId, date: entry.date, label: entry.title || entry.category || "Entry",
      status: (entry.progress?.transactionId ? "partial" : "waiting") as PendingSyncItem["status"],
      ...(entry.progress && canChangePending(key, entry) ? { needsReview: true } : {}),
    }));
    const receipts = readPendingReceipts().map(({ key, receipt }) => ({
      key,
      owner: receipt.owner,
      type: "Expense",
      amount: receipt.kind === "save" ? receipt.draft.expense.amount : "",
      accountId: receipt.kind === "save" ? receipt.draft.expense.accountId : "",
      date: receipt.kind === "save" ? receipt.draft.expense.date : new Date().toISOString().slice(0, 10),
      label: receipt.kind === "save"
        ? (receipt.draft.expense.title || "Receipt")
        : "Receipt photos",
      status: (receipt.kind === "save" && receipt.progress?.transactionId ? "partial" : "waiting") as PendingSyncItem["status"],
    }));
    const records = readPendingRecords().map(({ key, record }) => {
      if (record.kind === "loan") {
        return {
          key, owner: record.owner, type: "IOU", amount: record.draft.originalAmount,
          accountId: record.draft.fundingAccountId, date: record.draft.createdDate,
          label: record.draft.description || "IOU", status: "waiting" as const,
        };
      }
      if (record.kind === "settle") {
        return {
          key, owner: record.owner, type: "IOU", amount: record.body.amount,
          accountId: record.body.accountId, date: record.body.date,
          label: "IOU repayment", status: "waiting" as const,
        };
      }
      return {
        key, owner: record.owner, type: "Bill", amount: record.draft.amount,
        accountId: record.draft.accountId, date: record.draft.startDate,
        label: record.draft.name || "Recurring", status: "waiting" as const,
      };
    });
    return [...entries, ...receipts, ...records];
  } catch {
    return [];
  }
}
